import { ComponentType, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { Filters } from '@rossum/components/filtering';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconRefresh } from '@rossum/ui/icons/tabler';
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridEventListener,
  GridPaginationModel,
  GridRowParams,
  useGridApiRef,
} from '@rossum/ui/x-data-grid-pro';
import { pageSizeOptions } from '../../../constants/values';
import { useDataGridLocaleText } from '../../../i18n/useDataGridLocaleText';
import { RULES_EXECUTION_LOGS_QUERY } from '../../../redux/modules/localStorage/actions';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import useDebounce from '../../../utils/hooks/useDebounce';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { useRowCount } from '../../document-list-base/hooks/usePagination';
import { absoluteMaxSizeStyles } from '../../document-upload/components/helpers';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { notify } from '../../toaster';
import {
  getTransformedRulesExecutionLogs,
  rulesExecutionLogsDataGridStyles,
  TransformedRuleExecutionLog,
} from '../helpers';
import { useColumns } from '../hooks/useColumns';
import {
  QUERY_KEY_NEW_RULES_EXECUTION_LOGS_CHECK,
  useNewRulesExecutionLogsCheck,
} from '../hooks/useNewRulesExecutionLogsCheck';
import { useRulesExecutionLogs } from '../hooks/useRulesExecutionLogs';
import {
  rulesExecutionLogsQueryParser,
  useRulesExecutionLogsFilterSettings,
} from '../hooks/useRulesExecutionLogsFilterSettings';
import { LoadingSkeletons } from './EmptyStates';
import { PanelContent } from './log-row/PanelContent';
import { Row, ROW_HEIGHT, ROW_SPACING } from './log-row/Row';

const NEW_LOGS_TOAST_ID = 'new-rules-execution-logs-available';

type RulesExecutionLogsPageProps = {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  HeaderComponent: ComponentType<any>;
};

export const RulesExecutionLogsPage = ({
  HeaderComponent,
}: RulesExecutionLogsPageProps) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();

  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: rulesExecutionLogsQueryParser,
    localStorageKey: RULES_EXECUTION_LOGS_QUERY,
  });

  const {
    queues,
    workspaces,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const filterSettings = useRulesExecutionLogsFilterSettings({
    queues,
    workspaces,
    isQueuesLoading,
    setFilters: updateParsedParams,
  });

  const localeText = useDataGridLocaleText();

  // Debounce search because it's likely to be rapidly changing
  const debouncedSearch = useDebounce(parsedParams.search, 250);

  // Base query without pagination - used for new logs check
  const { page, pageSize, triggerMatch, ...restParams } = {
    ...parsedParams,
    search: debouncedSearch,
  };

  const logsQuery = {
    ...restParams,
    triggerMatch:
      triggerMatch === undefined ? undefined : triggerMatch === 'true',
  };

  const {
    data: logs,
    refetch: refetchLogs,
    isLoading,
    isRefetching,
    dataUpdatedAt,
  } = useRulesExecutionLogs({
    ...logsQuery,
    page: page ?? 1,
    pageSize: pageSize ?? 100,
  });

  const handleRefreshLogs = useCallback(() => {
    // Cancel in-flight polling queries to prevent stale data
    queryClient.cancelQueries({
      queryKey: [QUERY_KEY_NEW_RULES_EXECUTION_LOGS_CHECK],
    });

    // Dismiss the new logs toast since we're syncing
    toast.dismiss(NEW_LOGS_TOAST_ID);

    refetchLogs();
  }, [queryClient, refetchLogs]);

  // Check for new logs - only when on page 1, since comparing
  // the first item of page 2+ with page 1 would always show differences
  const firstLog = logs?.results?.[0];
  const latestKnownId = firstLog
    ? `${firstLog.requestId}-${firstLog.ruleId}-${firstLog.createdAt}`
    : undefined;
  const isOnFirstPage = (page ?? 1) === 1;

  useNewRulesExecutionLogsCheck({
    latestKnownId,
    logsQuery,
    enabled: !isLoading && isOnFirstPage,
    onNewLogs: () => {
      notify.default(
        {
          title: intl.formatMessage({
            id: 'features.rulesExecutionLogs.newLogsAvailable',
          }),
          id: NEW_LOGS_TOAST_ID,
          toastActions: [
            {
              label: intl.formatMessage({
                id: 'features.rulesExecutionLogs.header.refresh',
              }),
              onClick: handleRefreshLogs,
            },
          ],
        },
        { duration: Infinity }
      );
    },
  });

  // Dismiss the new logs toast when leaving the page or navigating away from page 1
  useEffect(() => {
    if (!isOnFirstPage) {
      toast.dismiss(NEW_LOGS_TOAST_ID);
    }
    return () => {
      toast.dismiss(NEW_LOGS_TOAST_ID);
    };
  }, [isOnFirstPage]);

  const rulesExecutionLogs = getTransformedRulesExecutionLogs(
    logs?.results ?? []
  );

  const { columns } = useColumns({ queues, workspaces });

  const rowCount = useRowCount(logs?.pagination?.total);

  const paginationModel = useMemo(
    () => ({
      page: (parsedParams.page ?? 1) - 1,
      pageSize: parsedParams.pageSize ?? 100,
    }),
    [parsedParams.page, parsedParams.pageSize]
  );

  const handleOnPaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) =>
      updateParsedParams({
        page: newPaginationModel.page + 1,
        pageSize: newPaginationModel.pageSize,
      }),
    [updateParsedParams]
  );

  // Functions getDetailPanelContent, getDetailPanelHeight and onRowClick have to be memoized
  // The Data Grid depends on the referential value of these props to cache their values and optimize the rendering.
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const getDetailPanelContent = useCallback(
    (props: GridRowParams<TransformedRuleExecutionLog>) => (
      <PanelContent {...props} />
    ),
    []
  );

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    params => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef]
  );

  return (
    <>
      <HelmetComponent translationKey="features.rulesExecutionLogs.header.title" />
      <PageLayout
        fullWidth
        renderHeader={({ scrollableDivRef }) => (
          <HeaderComponent
            scrollableDivRef={scrollableDivRef}
            title={intl.formatMessage({
              id: 'features.rulesExecutionLogs.header.title',
            })}
            description={intl.formatMessage({
              id: 'features.rulesExecutionLogs.header.description',
            })}
            buttons={[
              <SearchInput
                key="search-rules-execution-logs"
                value={parsedParams.search ?? ''}
                onChange={(search: string) => {
                  updateParsedParams({
                    ...parsedParams,
                    search: search.length > 0 ? search : undefined,
                  });
                }}
                placeholder={intl.formatMessage({
                  id: 'features.rulesExecutionLogs.search.placeholder',
                })}
              />,
              <Tooltip
                key="refresh-rules-execution-logs-button"
                title={
                  dataUpdatedAt
                    ? intl.formatMessage(
                        {
                          id: 'containers.settings.extensions.logs.dataUpdatedAt',
                        },
                        {
                          date: intl.formatDate(dataUpdatedAt, {
                            dateStyle: 'medium',
                            timeStyle: 'medium',
                          }),
                        }
                      )
                    : ''
                }
              >
                <Button
                  data-cy="refresh-rules-execution-logs-button"
                  variant="contained"
                  onClick={!isRefetching ? handleRefreshLogs : undefined}
                  startIcon={
                    <SvgIcon fontSize="large">
                      <IconRefresh />
                    </SvgIcon>
                  }
                >
                  {intl.formatMessage({
                    id: 'features.rulesExecutionLogs.header.refresh',
                  })}
                </Button>
              </Tooltip>,
            ]}
          />
        )}
      >
        <Box sx={{ position: 'absolute', width: '100%' }}>
          {isRefetching ? <LinearProgress /> : null}
        </Box>
        <Stack p={4} flex={1}>
          <Filters
            dataCy="rules-execution-logs-filters"
            filters={parsedParams}
            setFilters={updateParsedParams}
            filterSettings={filterSettings}
          />
          <Box position="relative" flexGrow={1} height={1}>
            <DataGridPro
              disableRowSelectionExcludeModel
              apiRef={apiRef}
              loading={isLoading}
              columns={columns}
              rows={rulesExecutionLogs}
              localeText={{
                ...localeText,
                noRowsLabel: intl.formatMessage({
                  id: 'features.rulesExecutionLogs.rows.empty',
                }),
                noResultsOverlayLabel: intl.formatMessage({
                  id: 'features.rulesExecutionLogs.rows.empty',
                }),
              }}
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnResize
              disableColumnReorder
              disableColumnFilter
              pagination
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={handleOnPaginationModelChange}
              pageSizeOptions={pageSizeOptions}
              rowHeight={ROW_HEIGHT + ROW_SPACING}
              onRowClick={onRowClick}
              getDetailPanelHeight={getDetailPanelHeight}
              getDetailPanelContent={getDetailPanelContent}
              slots={{
                loadingOverlay: () => <LoadingSkeletons />,
                row: Row,
                columnHeaders: ColumnHeaderPaper,
              }}
              slotProps={{
                row: { 'data-cy': 'rules-execution-log-row' },
                basePagination: {
                  // @ts-expect-error wrong type
                  labelRowsPerPage: intl.formatMessage({
                    id: 'containers.allDocuments.pagination.label',
                  }),
                  'data-cy': 'rules-execution-logs-pagination',
                  sx: {
                    display: rowCount === 0 ? 'none' : 'inherit',
                    justifyContent: 'flex-end',
                    [`> * p`]: {
                      margin: 0,
                    },
                  },
                },
                baseSwitch: {
                  // @ts-expect-error
                  color: 'secondary',
                },
              }}
              sx={{
                ...commonDataGridStyles,
                ...absoluteMaxSizeStyles,
                ...rulesExecutionLogsDataGridStyles,
                [`& .${gridClasses.cell}`]: {
                  display: 'flex',
                  alignItems: 'center',
                },
                [`& .${gridClasses.columnHeader}`]: {
                  borderBottom: 'none !important',
                },
              }}
            />
          </Box>
        </Stack>
      </PageLayout>
    </>
  );
};
